import { useEffect, useState } from 'react';
import {
  aboutContent as defaultAboutContent,
  faqData as defaultFaqData,
  resources as defaultResources,
  specialties as defaultSpecialties,
  testimonials as defaultTestimonials,
} from '../mockData';

const DEFAULT_CORE_VALUES = [
  {
    title: 'Compassion',
    description: 'Providing empathetic, understanding support throughout your wellness journey',
  },
  {
    title: 'Professionalism',
    description: 'Maintaining the highest standards of care and ethical practice',
  },
  {
    title: 'Accessibility',
    description: 'Making wellness services available both locally and worldwide online',
  },
];

const DEFAULT_SERVICES_PAGE = {
  heroTitle: {
    en: 'Professional Wellness Services',
    de: 'Professionelle Wellness-Leistungen',
    fr: 'Services de bien-être professionnels',
    es: 'Servicios profesionales de bienestar',
  },
  heroSubtitle: {
    en: 'Comprehensive mental health coaching and therapeutic massage services designed to support your unique journey towards wellbeing',
    de: 'Umfassendes Mental-Health-Coaching und therapeutische Massage zur Unterstützung Ihres individuellen Weges.',
    fr: 'Coaching en santé mentale et massage thérapeutique pour soutenir votre parcours personnel.',
    es: 'Coaching de salud mental y masaje terapéutico para apoyar tu proceso personal.',
  },
  comparisonTitle: {
    en: 'Service Comparison',
    de: 'Leistungsvergleich',
    fr: 'Comparaison des services',
    es: 'Comparación de servicios',
  },
  faqTitle: {
    en: 'Frequently Asked Questions',
    de: 'Häufige Fragen',
    fr: 'Questions fréquentes',
    es: 'Preguntas frecuentes',
  },
  ctaTitle: {
    en: 'Ready to Get Started?',
    de: 'Bereit loszulegen?',
    fr: 'Prêt(e) à vous lancer ?',
    es: '¿Listo/a para empezar?',
  },
  ctaSubtitle: {
    en: 'Take the first step towards better mental and physical wellbeing. Book your session today.',
    de: 'Machen Sie den ersten Schritt zu besserem mentalem und körperlichem Wohlbefinden.',
    fr: 'Faites le premier pas vers un meilleur équilibre mental et physique.',
    es: 'Da el primer paso hacia un mejor bienestar mental y físico.',
  },
  ctaButton: {
    en: 'Book Your Session',
    de: 'Sitzung buchen',
    fr: 'Réserver votre séance',
    es: 'Reservar tu sesión',
  },
};

const DEFAULT_SECTIONS_CONTENT = {
  about: {
    title: defaultAboutContent.title,
    subtitle: defaultAboutContent.subtitle,
    image: defaultAboutContent.image,
    bio: defaultAboutContent.bio,
    approachParagraph:
      "I believe in a holistic approach that addresses both mental and physical wellbeing. Through personalized coaching sessions and therapeutic massage, I help clients develop sustainable strategies for managing their mental health while nurturing their body's natural healing abilities.",
    credentials: defaultAboutContent.credentials,
    languages: defaultAboutContent.languages,
    serviceAreaLine1: 'Munich (In-person)',
    serviceAreaLine2: 'Worldwide (Online)',
    commitmentText:
      'I am committed to ongoing professional development and staying current with the latest research and techniques in mental health coaching and therapeutic massage. Your wellbeing is my priority.',
    philosophyTitle: 'My Philosophy',
    philosophyParagraph1:
      "Every individual's journey is unique. I provide a safe, non-judgmental space where you can explore your challenges, develop coping strategies, and work towards your goals at your own pace.",
    philosophyParagraph2:
      "Whether you're dealing with depression, PTSD, ADHD, or simply seeking better balance in your life, I'm here to support you every step of the way.",
  },
  specialties: defaultSpecialties,
  testimonials: defaultTestimonials,
  coreValues: DEFAULT_CORE_VALUES,
  faqs: defaultFaqData,
  resources: defaultResources,
  servicesPage: DEFAULT_SERVICES_PAGE,
  publicPageCopy: {},
};

const fetchSectionsContent = async () => {
  const response = await fetch('/api/content/sections', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load sections content: ${response.status}`);
  }

  return response.json();
};

export const useSectionsContent = () => {
  const [sectionsContent, setSectionsContent] = useState(DEFAULT_SECTIONS_CONTENT);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      try {
        const payload = await fetchSectionsContent();
        if (isCancelled || !payload || typeof payload !== 'object') {
          return;
        }

        setSectionsContent((current) => ({
          ...current,
          ...payload,
          about: {
            ...current.about,
            ...(payload.about || {}),
          },
          servicesPage: {
            ...current.servicesPage,
            ...(payload.servicesPage || {}),
          },
          publicPageCopy: {
            ...current.publicPageCopy,
            ...(payload.publicPageCopy || {}),
          },
        }));
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load sections content from backend:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, []);

  return {
    sectionsContent,
    isLoading,
  };
};
